import React, { useCallback, useEffect } from 'react';

import { Divider, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';

import { IServerOrder } from '@rbi-ctg/menu';
import OrderCancelledModal from 'components/order-cancelled-modal';
import { IDeliveryOrderFragment } from 'generated/graphql-gateway';
import useDialogModal from 'hooks/use-dialog-modal';
import { useEtaCounter } from 'hooks/use-eta-counter/';
import { t } from 'state/intl/translations';
import { DeliveryStatus } from 'state/order/types';
import { routes } from 'utils/routing';

import CourierDetail from './courier-detail';
import DeliveryInfo from './delivery-order-card/delivery-info';
import DeliveryOrderDetails from './delivery-order-card/delivery-order-details';
import DeliveryProgress from './delivery-progress';
import { CardHeading, CardHeadingContainer, DeliveryCardContainer } from './styled';

interface IDeliveryDetailsProps {
  delivery: IDeliveryOrderFragment['delivery'];
  serverOrder: IServerOrder;
}

const DeliveryDetails: React.FC<React.PropsWithChildren<IDeliveryDetailsProps>> = ({
  delivery,
  serverOrder,
}) => {
  const { status: deliveryStatus, driver, dropoff } = delivery ?? {};

  const eta = useEtaCounter(deliveryStatus, dropoff);
  const router = useRouter();

  // @ts-ignore
  const [CancellationModal, openCancellationModal] = useDialogModal({
    Component: OrderCancelledModal,
    onDismiss: () => router.replace(routes.base),
  });

  useEffect(() => {
    if (deliveryStatus) {
      if (deliveryStatus === DeliveryStatus.ORDER_CANCELLED) {
        openCancellationModal();
      }
    }
  }, [deliveryStatus, openCancellationModal, serverOrder]);

  const getDeliveryEtaText = useCallback(
    (status: DeliveryStatus): string => {
      switch (status) {
        case DeliveryStatus.DRIVER_AT_CUSTOMER:
          return t('deliveryDriverArrived');
        case DeliveryStatus.ORDER_DROPPED_OFF:
          return t('deliveryDelivered');
        default:
          return eta || '...';
      }
    },
    [eta]
  );

  return (
    <>
      {!!deliveryStatus && (
        <DeliveryCardContainer>
          <CardHeadingContainer focusable>
            <Text>
              {`${t('eta')}: `}
              <Text testID="order-preparation-confirmation-eta">
                {getDeliveryEtaText(deliveryStatus)}
              </Text>
            </Text>
          </CardHeadingContainer>

          <Divider />

          {!!dropoff && (
            <CardHeadingContainer focusable>
              <CardHeading testID="order-preparation-confirmation" data-private>
                {`${t('deliveringToYourDoorAt')}\n`}
                {dropoff.addressLine1}
              </CardHeading>
            </CardHeadingContainer>
          )}

          <DeliveryProgress deliveryStatus={deliveryStatus} />

          {!!driver && (
            <CardHeadingContainer>
              <CourierDetail driver={driver} />
            </CardHeadingContainer>
          )}

          <DeliveryInfo serverOrder={serverOrder} />

          <Divider />

          <DeliveryOrderDetails serverOrder={serverOrder} />
        </DeliveryCardContainer>
      )}

      <CancellationModal />
    </>
  );
};

export default DeliveryDetails;
